import { Globe, Linkedin, Phone } from "lucide-react";
import { useRecordContext, useTranslate } from "ra-core";
import {
  EditButton,
  ReferenceField,
  ShowButton,
  TextField,
  DateField,
  UrlField,
  SelectField,
} from "@/components/admin";

import { AsideSection } from "../misc/AsideSection";
import { SaleName } from "../sales/SaleName";
import type { Company } from "../types";
import { sizes } from "./sizes";

interface CompanyAsideProps {
  link?: string;
}

export const CompanyAside = ({ link = "edit" }: CompanyAsideProps) => {
  const record = useRecordContext<Company>();
  const translate = useTranslate();
  
  if (!record) return null;

  return (
    <div className="hidden sm:block w-[250px] min-w-[250px] space-y-4">
      <div className="flex flex-row space-x-1">
        {link === "edit" ? (
          <EditButton label={translate("simplecrm.companies.edit_company", { _: "Editar Empresa" })} />
        ) : (
          <ShowButton label={translate("simplecrm.companies.show_company", { _: "Ver Empresa" })} />
        )}
      </div>

      <CompanyInfo record={record} />

      <AddressInfo record={record} />

      <ContextInfo record={record} />

      <AdditionalInfo record={record} />
    </div>
  );
};

const CompanyInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  
  if (!record.website && !record.linkedin_url && !record.phone_number) {
    return null;
  }

  return (
    <AsideSection title={translate("simplecrm.companies.company_info", { _: "Informações da Empresa" })}>
      {record.website && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Globe className="w-4 h-4" />
          <UrlField
            source="website"
            target="_blank"
            rel="noopener"
            content={record.website
              .replace("http://", "")
              .replace("https://", "")}
          />
        </div>
      )}
      {record.linkedin_url && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Linkedin className="w-4 h-4" />
          <a
            className="underline hover:no-underline"
            href={record.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            title={record.linkedin_url}
          >
            LinkedIn
          </a>
        </div>
      )}
      {record.phone_number && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Phone className="w-4 h-4" />
          <TextField source="phone_number" />
        </div>
      )}
    </AsideSection>
  );
};

const ContextInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  
  if (!record.revenue && !record.id) {
    return null;
  }

  return (
    <AsideSection title={translate("simplecrm.companies.context", { _: "Contexto" })}>
      {record.sector && (
        <span>
          {translate("simplecrm.companies.sector", { _: "Setor" })}: <TextField source="sector" />
        </span>
      )}
      {record.size && (
        <span>
          {translate("simplecrm.companies.size", { _: "Tamanho" })}: <SelectField source="size" choices={sizes} />
        </span>
      )}
      {record.revenue && (
        <span>
          {translate("simplecrm.companies.revenue", { _: "Receita" })}: <TextField source="revenue" />
        </span>
      )}
      {record.tax_identifier && (
        <span>
          {translate("simplecrm.companies.tax_identifier", { _: "Identificador Fiscal" })}: <TextField source="tax_identifier" />
        </span>
      )}
    </AsideSection>
  );
};

const AddressInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  
  if (!record.address && !record.city && !record.zipcode && !record.stateAbbr) {
    return null;
  }

  return (
    <AsideSection title={translate("simplecrm.companies.main_address", { _: "Endereço Principal" })} noGap>
      <TextField source="address" />
      <TextField source="city" />
      <TextField source="zipcode" />
      <TextField source="stateAbbr" />
      <TextField source="country" />
    </AsideSection>
  );
};

const AdditionalInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  
  if (
    !record.created_at &&
    !record.sales_id &&
    !record.description &&
    !record.context_links
  ) {
    return null;
  }
  const getBaseURL = (url: string) => {
    const urlObject = new URL(url.startsWith("http") ? url : `https://${url}`);
    return urlObject.hostname;
  };

  return (
    <AsideSection title={translate("simplecrm.companies.additional_info", { _: "Informações Adicionais" })}>
      {record.description && (
        <p className="text-sm  mb-1">{record.description}</p>
      )}
      {record.context_links && (
        <div className="flex flex-col">
          {record.context_links.map((link, index) =>
            link ? (
              <a
                key={index}
                className="text-sm underline hover:no-underline mb-1"
                href={link.startsWith("http") ? link : `https://${link}`}
                target="_blank"
                rel="noopener noreferrer"
                title={link}
              >
                {getBaseURL(link)}
              </a>
            ) : null,
          )}
        </div>
      )}
      {record.sales_id !== null && (
        <div className="inline-flex text-sm text-muted-foreground mb-1">
          {translate("simplecrm.companies.followed_by", { _: "Seguido por" })}&nbsp;
          <ReferenceField source="sales_id" reference="sales" record={record}>
            <SaleName />
          </ReferenceField>
        </div>
      )}
      {record.created_at && (
        <p className="text-sm text-muted-foreground mb-1">
          {translate("simplecrm.companies.added_on", { _: "Adicionado em" })}{" "}
          <DateField
            source="created_at"
            record={record}
            options={{
              year: "numeric",
              month: "long",
              day: "numeric",
            }}
          />
        </p>
      )}
    </AsideSection>
  );
};
